import React, { useState, useRef, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { makePageSpaceChecker } from '../pdfPageSpace';
import { Chart, ChartConfiguration } from 'chart.js/auto';

interface AdvancedReportGeneratorProps {
  chartData: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
    }>;
  };
  registrosPorObra: Array<{
    obra: string;
    registros: any[];
    total: number;
    completados: number;
    pendientes: number;
  }>;
  fechaGeneracion: string;
}

const AdvancedReportGenerator: React.FC<AdvancedReportGeneratorProps> = ({
  chartData,
  registrosPorObra,
  fechaGeneracion
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [paginaGrafico, setPaginaGrafico] = useState(0);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (chartRef.current && chartData && chartData.labels.length > 0) {
      // Destruir gráfico anterior si existe
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        // Calcular datos para la página actual
        const diasPorPagina = 7;
        const inicioPagina = paginaGrafico * diasPorPagina;
        const finPagina = Math.min(inicioPagina + diasPorPagina, chartData.labels.length);
        
        const labelsPagina = chartData.labels.slice(inicioPagina, finPagina);
        const dataPagina = chartData.datasets[0].data.slice(inicioPagina, finPagina);
        
        const config: ChartConfiguration = {
          type: 'line',
          data: {
            labels: labelsPagina,
            datasets: [{
              label: 'Registros de Limpieza',
              data: dataPagina,
              borderColor: 'rgb(59, 130, 246)',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: `Tendencia de Registros - Página ${paginaGrafico + 1} de ${Math.ceil(chartData.labels.length / diasPorPagina)}`,
                font: {
                  size: 16
                }
              },
              legend: {
                display: true,
                position: 'top'
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                title: {
                  display: true,
                  text: 'Cantidad de Registros'
                }
              },
              x: {
                title: {
                  display: true,
                  text: 'Fecha'
                }
              }
            }
          }
        };

        chartInstance.current = new Chart(ctx, config);
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, paginaGrafico]);

  // Reset de página cuando cambie chartData
  useEffect(() => {
    setPaginaGrafico(0);
  }, [chartData]);

  const generarReporteAvanzadoPDF = async () => {
    if (!chartData || chartData.labels.length === 0) return;
    
    setIsGenerating(true);
    
    try {
      const doc = new jsPDF('landscape', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      
      // Función auxiliar para verificar espacio y agregar página si es necesario
      const checkPageSpace = makePageSpaceChecker(doc, pageHeight, margin, () => yPosition, (y) => { yPosition = y; });
      
      // Configuración del documento
      doc.setFont('helvetica');
      doc.setFontSize(20);
      
      // Título principal
      doc.text('Reporte Avanzado - Análisis de Tendencias y Métricas', 20, 30);
      
      // Fecha de generación
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generado el: ${fechaGeneracion}`, 20, 45);
      
      // Línea separadora
      doc.setDrawColor(200, 200, 200);
      doc.line(20, 55, 277, 55);
      
      let yPosition = 75;
      
      // Resumen Ejecutivo
      checkPageSpace(40);
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text('Resumen Ejecutivo', 20, yPosition);
      yPosition += 20;
      
      // Análisis estadístico avanzado
      if (chartData && chartData.labels.length > 0) {
        const datos = chartData.datasets[0].data;
        const totalRegistros = datos.reduce((sum, valor) => sum + valor, 0);
        const promedio = totalRegistros / datos.length;
        const maximo = Math.max(...datos);
        const minimo = Math.min(...datos);
        
        // Calcular estadísticas avanzadas
        const sortedData = [...datos].sort((a, b) => a - b);
        const mediana = sortedData.length % 2 === 0 ? 
          (sortedData[sortedData.length / 2 - 1] + sortedData[sortedData.length / 2]) / 2 :
          sortedData[Math.floor(sortedData.length / 2)];
        
        const variabilidad = datos.reduce((sum, valor) => sum + Math.pow(valor - promedio, 2), 0) / datos.length;
        const desviacionEstandar = Math.sqrt(variabilidad);
        const coeficienteVariacion = (desviacionEstandar / promedio) * 100;
        
        // Calcular percentiles
        const p25 = sortedData[Math.floor(sortedData.length * 0.25)];
        const p75 = sortedData[Math.floor(sortedData.length * 0.75)];
        const rangoIntercuartil = p75 - p25;
        
        const estadisticasAvanzadas = [
          ['Total de Registros', totalRegistros.toString()],
          ['Promedio', promedio.toFixed(2)],
          ['Mediana', mediana.toFixed(2)],
          ['Máximo', maximo.toString()],
          ['Mínimo', minimo.toString()],
          ['Desviación Estándar', desviacionEstandar.toFixed(2)],
          ['Coeficiente de Variación', `${coeficienteVariacion.toFixed(2)}%`],
          ['Percentil 25', p25.toString()],
          ['Percentil 75', p75.toString()],
          ['Rango Intercuartil', rangoIntercuartil.toString()]
        ];
        
        checkPageSpace(estadisticasAvanzadas.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Métrica Estadística', 'Valor']],
          body: estadisticasAvanzadas,
          theme: 'grid',
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (estadisticasAvanzadas.length * 12 + 50);
      }
      
      // Análisis de tendencias por obra
      if (registrosPorObra && registrosPorObra.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Análisis de Eficiencia por Obra', 20, yPosition);
        yPosition += 20;
        
        const obraData = registrosPorObra.map(grupo => {
          const porcentajeCompletado = grupo.total > 0 ? 
            ((grupo.completados / grupo.total) * 100).toFixed(1) : '0';
          const eficiencia = grupo.total > 0 ? 
            ((grupo.completados / grupo.total) * 100) : 0;
          
          let estadoEficiencia = '';
          if (eficiencia >= 90) estadoEficiencia = 'Excelente';
          else if (eficiencia >= 75) estadoEficiencia = 'Buena';
          else if (eficiencia >= 60) estadoEficiencia = 'Regular';
          else estadoEficiencia = 'Necesita Mejora';
          
          return [
            grupo.obra,
            grupo.total.toString(),
            grupo.completados.toString(),
            grupo.pendientes.toString(),
            `${porcentajeCompletado}%`,
            estadoEficiencia
          ];
        });
        
        checkPageSpace(obraData.length * 10 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Obra', 'Total', 'Completados', 'Pendientes', '% Eficiencia', 'Estado']],
          body: obraData,
          theme: 'grid',
          headStyles: { fillColor: [34, 197, 94], textColor: 255 },
          styles: { fontSize: 8 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (obraData.length * 10 + 50);
      }
      
      // Análisis de patrones temporales
      if (chartData && chartData.labels.length > 0) {
        checkPageSpace(40);
        doc.setFontSize(16);
        doc.text('Análisis de Patrones Temporales', 20, yPosition);
        yPosition += 20;
        
        const datos = chartData.datasets[0].data;
        
        // Identificar días de mayor y menor actividad
        const diasConMasRegistros = chartData.labels[datos.indexOf(Math.max(...datos))];
        const diasConMenosRegistros = chartData.labels[datos.indexOf(Math.min(...datos))];
        
        // Calcular tendencia de crecimiento
        const crecimientoPromedioDiario = datos.slice(1).reduce((sum, valor, index) => {
          return sum + (valor - datos[index]);
        }, 0) / (datos.length - 1);
        
        // Identificar picos y valles
        const picos = datos.filter((valor, index) => {
          if (index === 0 || index === datos.length - 1) return false;
          return valor > datos[index - 1] && valor > datos[index + 1];
        }).length;
        
        const valles = datos.filter((valor, index) => {
          if (index === 0 || index === datos.length - 1) return false;
          return valor < datos[index - 1] && valor < datos[index + 1];
        }).length;
        
        const patronesTemporales = [
          ['Día con mayor actividad', diasConMasRegistros],
          ['Día con menor actividad', diasConMenosRegistros],
          ['Crecimiento promedio diario', crecimientoPromedioDiario.toFixed(2)],
          ['Número de picos', picos.toString()],
          ['Número de valles', valles.toString()],
          ['Estabilidad temporal', picos + valles < 3 ? 'Alta' : picos + valles < 5 ? 'Media' : 'Baja']
        ];
        
        checkPageSpace(patronesTemporales.length * 12 + 30);
        autoTable(doc, {
          startY: yPosition,
          head: [['Patrón Temporal', 'Valor']],
          body: patronesTemporales,
          theme: 'grid',
          headStyles: { fillColor: [147, 51, 234], textColor: 255 },
          styles: { fontSize: 9 },
          margin: { top: 20, bottom: 20 }
        });
        
        yPosition = (doc as any).lastAutoTable?.finalY + 20 || yPosition + (patronesTemporales.length * 12 + 50);
      }
      
      // Recomendaciones estratégicas
      checkPageSpace(40);
      doc.setFontSize(16);
      doc.text('Recomendaciones Estratégicas', 20, yPosition);
      yPosition += 20;
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      
      if (chartData && chartData.labels.length > 0) {
        const datos = chartData.datasets[0].data;
        const promedio = datos.reduce((sum, valor) => sum + valor, 0) / datos.length;
        const variabilidad = datos.reduce((sum, valor) => sum + Math.pow(valor - promedio, 2), 0) / datos.length;
        const desviacionEstandar = Math.sqrt(variabilidad);
        const coeficienteVariacion = (desviacionEstandar / promedio) * 100;
        
        if (coeficienteVariacion > 50) {
          checkPageSpace(15);
          doc.text('• Implementar horarios estandarizados para reducir variabilidad en la carga de trabajo.', 20, yPosition);
          yPosition += 15;
        }
        
        if (datos[datos.length - 1] < promedio * 0.8) {
          checkPageSpace(15);
          doc.text('• Investigar causas de la disminución reciente en registros y tomar medidas correctivas.', 20, yPosition);
          yPosition += 15;
        }
        
        if (datos[datos.length - 1] > promedio * 1.3) {
          checkPageSpace(15);
          doc.text('• Evaluar si el incremento en registros es sostenible y planificar recursos adicionales.', 20, yPosition);
          yPosition += 15;
        }
        
        checkPageSpace(15);
        doc.text('• Establecer métricas de rendimiento basadas en los patrones identificados.', 20, yPosition);
        yPosition += 15;
        
        checkPageSpace(15);
        doc.text('• Implementar sistema de alertas para desviaciones significativas de las tendencias normales.', 20, yPosition);
        yPosition += 15;
      }
      
      // Pie de página
      checkPageSpace(20);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Sistema de Control de Limpieza - ControlLimpio - Reporte Avanzado', 20, yPosition);
      yPosition += 5;
      doc.text('Análisis estadístico y de tendencias generado automáticamente', 20, yPosition);
      
      // Guardar el PDF
      const nombreArchivo = `Reporte_Avanzado_Tendencias_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(nombreArchivo);
      
    } catch (error) {
      console.error('Error al generar el reporte avanzado PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-indigo-50 to-cyan-50 shadow rounded-lg p-6 mb-6 border border-indigo-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-indigo-900">Reporte Avanzado con Gráficos</h3>
          <p className="text-sm text-indigo-700">
            Genera reportes avanzados con análisis estadístico detallado y visualizaciones
          </p>
        </div>
        <button
          onClick={generarReporteAvanzadoPDF}
          disabled={isGenerating || !chartData || chartData.labels.length === 0}
          className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${
            isGenerating || !chartData || chartData.labels.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
          }`}
        >
          {isGenerating ? (
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generando...
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Generar Reporte Avanzado
            </div>
          )}
        </button>
      </div>
      
      {/* Vista previa del gráfico */}
      {chartData && chartData.labels.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-indigo-800">Vista Previa del Gráfico de Tendencias:</h4>
            {chartData.labels.length > 7 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPaginaGrafico(Math.max(0, paginaGrafico - 1))}
                  disabled={paginaGrafico === 0}
                  className="px-2 py-1 text-xs bg-indigo-200 text-indigo-700 rounded hover:bg-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ←
                </button>
                <span className="text-xs text-indigo-600">
                  {Math.floor(paginaGrafico / 7) + 1} de {Math.ceil(chartData.labels.length / 7)}
                </span>
                <button
                  onClick={() => setPaginaGrafico(Math.min(Math.ceil(chartData.labels.length / 7) - 1, paginaGrafico + 1))}
                  disabled={paginaGrafico >= Math.ceil(chartData.labels.length / 7) - 1}
                  className="px-2 py-1 text-xs bg-indigo-200 text-indigo-700 rounded hover:bg-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  →
                </button>
              </div>
            )}
          </div>
          <div className="bg-white p-4 rounded-lg border border-indigo-200">
            <canvas ref={chartRef} height="200"></canvas>
          </div>
        </div>
      )}
      
      {(!chartData || chartData.labels.length === 0) && (
        <div className="text-center py-4">
          <div className="text-indigo-500 text-sm">
            No hay datos disponibles para generar el reporte avanzado
          </div>
        </div>
      )}
      
      {chartData && chartData.labels.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
            <div className="font-medium text-indigo-900">Análisis Estadístico</div>
            <div className="text-indigo-700">✓ Mediana, percentiles, CV</div>
          </div>
          <div className="bg-cyan-50 p-3 rounded-md border border-cyan-200">
            <div className="font-medium text-cyan-900">Patrones Temporales</div>
            <div className="text-cyan-700">✓ Picos, valles, estabilidad</div>
          </div>
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="font-medium text-blue-900">Eficiencia por Obra</div>
            <div className="text-blue-700">✓ Estados y clasificaciones</div>
          </div>
          <div className="bg-purple-50 p-3 rounded-md border border-purple-200">
            <div className="font-medium text-purple-900">Recomendaciones</div>
            <div className="text-purple-700">✓ Estratégicas y operativas</div>
          </div>
          <div className="bg-green-50 p-3 rounded-md border border-green-200">
            <div className="font-medium text-green-900">Visualización</div>
            <div className="text-green-700">✓ Gráficos interactivos</div>
          </div>
          <div className="bg-orange-50 p-3 rounded-md border border-orange-200">
            <div className="font-medium text-orange-900">Formato Avanzado</div>
            <div className="text-orange-700">✓ PDF horizontal detallado</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedReportGenerator;
